import { GridButton, GridOverflowButton } from '../grid.types';

export interface SortGridButtonsByOrderByOptions {
    dangerLastForUnordered?: boolean;
}

export interface GroupGridButtonsByOverflowOptions {
    renderSyntheticOverflowAtEnd?: boolean;
    renderSyntheticOverflowAtStart?: boolean;
    syntheticOverflowTooltipOnly?: boolean;
}

export function sortGridButtonsByOrderBy<TButton extends GridButton>(buttons: TButton[], options: SortGridButtonsByOrderByOptions = {}): TButton[] {
    return buttons
        .map((button, index) => ({ button, index }))
        .sort((a, b) => {
            const orderDiff = getGridButtonOrderBy(a.button) - getGridButtonOrderBy(b.button);

            if (orderDiff !== 0) return orderDiff;

            if (options.dangerLastForUnordered === true && !hasGridButtonOrderBy(a.button) && !hasGridButtonOrderBy(b.button)) {
                const dangerDiff = getDangerSortValue(a.button) - getDangerSortValue(b.button);

                if (dangerDiff !== 0) return dangerDiff;
            }

            return a.index - b.index;
        })
        .map(({ button }) => button);
}

export function groupGridButtonsByOverflow<TButton extends GridButton>(buttons: TButton[], options: GroupGridButtonsByOverflowOptions = {}): (TButton | GridOverflowButton<TButton>)[] {
    const groups = new Map<string, { overflow: GridOverflowButton<TButton>; buttons: TButton[]; rendered: boolean; hasExplicitOverflow: boolean }>();

    for (const button of buttons) {
        if (!button.overflow_name) continue;

        if (!groups.has(button.overflow_name)) {
            const overflowButton = buttons.find((item) => item.name === button.overflow_name) as GridOverflowButton<TButton> | undefined;

            groups.set(button.overflow_name, {
                overflow: overflowButton ?? getSyntheticOverflowButton(button.overflow_name, options),
                buttons: [],
                rendered: false,
                hasExplicitOverflow: !!overflowButton,
            });
        }

        groups.get(button.overflow_name)!.buttons.push(button);
    }

    for (const group of groups.values()) {
        group.overflow.type = 'overflow';
        group.overflow.buttons = group.buttons;
    }

    const groupedButtons: (TButton | GridOverflowButton<TButton>)[] = [];

    if (options.renderSyntheticOverflowAtStart === true) {
        for (const group of groups.values()) {
            if (group.hasExplicitOverflow !== true && group.rendered !== true) {
                groupedButtons.push(group.overflow);
                group.rendered = true;
            }
        }
    }

    for (const button of buttons) {
        const explicitOverflowGroup = groups.get(button.name);

        if (explicitOverflowGroup?.hasExplicitOverflow === true && explicitOverflowGroup.overflow.name === button.name) {
            groupedButtons.push(explicitOverflowGroup.overflow);
            explicitOverflowGroup.rendered = true;
            continue;
        }

        if (button.overflow_name) {
            const group = groups.get(button.overflow_name);

            if (group && group.hasExplicitOverflow !== true && group.rendered !== true && options.renderSyntheticOverflowAtEnd !== true) {
                groupedButtons.push(group.overflow);
                group.rendered = true;
            }

            continue;
        }

        groupedButtons.push(button);
    }

    if (options.renderSyntheticOverflowAtEnd === true) {
        for (const group of groups.values()) {
            if (group.hasExplicitOverflow !== true && group.rendered !== true) {
                groupedButtons.push(group.overflow);
                group.rendered = true;
            }
        }
    }

    return groupedButtons;
}

export function getGridButtonOrderBy(button: GridButton): number {
    const orderby = Number(button.orderby);

    return Number.isFinite(orderby) ? orderby : 0;
}

export function hasGridButtonOrderBy(button: GridButton): boolean {
    if (button.orderby === null || button.orderby === undefined || `${button.orderby}`.trim() === '') return false;

    return Number.isFinite(Number(button.orderby));
}

function getDangerSortValue(button: GridButton): number {
    const type = button.btn_type ?? button.type;

    return type === 'danger' ? 1 : 0;
}

function getSyntheticOverflowButton<TButton extends GridButton>(name: string, options: GroupGridButtonsByOverflowOptions): GridOverflowButton<TButton> {
    if (options.syntheticOverflowTooltipOnly === true) {
        return { type: 'overflow', name, tooltip: 'tt_grid_overflow_menu_tooltip', buttons: [] };
    }

    return { type: 'overflow', name, text: 'tt_grid_overflow_menu_tooltip', buttons: [] };
}
